//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import {
  BodyBoldText,
  BodySecondaryText,
  BodyText,
} from '@components/atoms/text/Text';
import Popover from '@components/molecules/popover/Popover';
import { getIsUiaVideoClosedCaptionsEnabled } from '@legacy-components/common/universal-image-actions/selectors/getIsUiaVideoClosedCaptionsEnabled';
import {
  MarketoIntegration,
  MarketoIntegrationModalStyle,
} from '@marketo-integration/MarketoIntegration';
import { MarketoIntegrationFeature } from '@marketo-integration/MarketoIntegrationFeature';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';

import { captionTooltip } from 'app/constants/mute-tooltip-content';

export interface UniversalImageActionsCaptionButtonProps {
  isDisabled: boolean;
  isVideoCaptioned: boolean;
  onClickCaptionButton: () => void;
}

const UniversalImageActionsCaptionButton = ({
  isDisabled,
  isVideoCaptioned,
  onClickCaptionButton,
}: UniversalImageActionsCaptionButtonProps) => {
  const isUiaVideoClosedCaptionsEnabled = useSelector(
    getIsUiaVideoClosedCaptionsEnabled,
  );

  return (
    <>
      <Popover
        target={
          <StyledActionButton
            iconName={
              isVideoCaptioned ? 'closed-caption-on' : 'closed-caption-off'
            }
            onClick={onClickCaptionButton}
            data-status={isVideoCaptioned}
            isDisabled={isDisabled || !isUiaVideoClosedCaptionsEnabled}
          />
        }
        content={
          <PopoverContentContainer>
            {isUiaVideoClosedCaptionsEnabled && (
              <BodyBoldText>
                {isVideoCaptioned ? 'Turn off captions ' : 'Turn on captions '}
              </BodyBoldText>
            )}
            {!isVideoCaptioned &&
              (isUiaVideoClosedCaptionsEnabled ? (
                <BodySecondaryText>{captionTooltip}</BodySecondaryText>
              ) : (
                <BodyText>
                  Upgrade to unblock the ability to add close captions to
                  videos.&nbsp;
                  <MarketoIntegration
                    feature={MarketoIntegrationFeature.UIAVideoClosedCaptions}
                  />
                </BodyText>
              ))}
          </PopoverContentContainer>
        }
        placement='bottom'
      />
      <MarketoIntegrationModalStyle />
    </>
  );
};

angular.module('curalate').component('universalImageActionsCaptionButton', {
  bindings: {
    isDisabled: '<',
    onClickCaptionButton: '&',
    isVideoCaptioned: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: UniversalImageActionsCaptionButtonProps } & IScope) => {
      return (
        <UniversalImageActionsCaptionButton
          isDisabled={scope.$ctrl.isDisabled}
          onClickCaptionButton={applyScope(
            scope,
            scope.$ctrl.onClickCaptionButton,
          )}
          isVideoCaptioned={scope.$ctrl.isVideoCaptioned}
        />
      );
    },
  ),
});

const StyledActionButton = styled(ActionButton)(
  ({ theme }) => `
    :hover {
      
      button[disabled] {
        background-color: ${theme.colors.white};
        
        svg {
          color: ${theme.colors.gray7};
        }
      }
    }
  `,
);

const PopoverContentContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[2]};
    max-width: ${theme.smallMeasure};
  `,
);
